import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { FeatureFlagForm } from '@/features/feature-flags/FeatureFlagForm';
import { useFeatureFlags } from '@/queries/featureFlags';
import { useApiError } from '@/lib/useApiError';
import {
  HIDDEN_FEATURE_FLAG_KEYS,
  type FeatureFlag,
} from '@/schemas/admin/featureFlags';

export const Route = createFileRoute('/_authed/feature-flags')({
  component: FeatureFlagsScreen,
});

function FeatureFlagsScreen() {
  const { t } = useTranslation('featureFlags');
  const { t: tc } = useTranslation('common');
  const list = useFeatureFlags();
  const resolveError = useApiError();
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<FeatureFlag | null>(null);

  // Hide launch_mode — Phase 5 owns it.
  const visibleFlags = React.useMemo(
    () => (list.data ?? []).filter((f) => !HIDDEN_FEATURE_FLAG_KEYS.has(f.key)),
    [list.data]
  );

  const columns: readonly DataColumn<FeatureFlag>[] = [
    {
      id: 'key',
      header: t('table.key'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-sm font-medium">
          {r.key}
        </span>
      ),
      sortBy: (r) => r.key,
    },
    {
      id: 'enabled',
      header: t('table.enabled'),
      cell: (r) =>
        r.enabled ? (
          <Badge tone="success">{t('form.enabled')}</Badge>
        ) : (
          <Badge tone="neutral">—</Badge>
        ),
      sortBy: (r) => (r.enabled ? 1 : 0),
    },
    {
      id: 'rollout',
      header: t('table.rollout'),
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span dir="ltr" className="font-mono text-xs">
            {r.rollout_percent}%
          </span>
          <span
            aria-hidden
            className="block h-1.5 w-24 rounded-full bg-[color:var(--color-surface-2)]"
          >
            <span
              className="block h-full rounded-full bg-[color:var(--color-accent)]"
              style={{ width: `${r.rollout_percent}%` }}
            />
          </span>
        </div>
      ),
      sortBy: (r) => r.rollout_percent,
    },
    {
      id: 'scope',
      header: t('table.scope'),
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.locales ?? []).map((l) => (
            <Badge key={`l-${l}`} tone="neutral">
              {l}
            </Badge>
          ))}
          {(r.regions ?? []).map((rg) => (
            <Badge key={`r-${rg}`} tone="neutral">
              {rg}
            </Badge>
          ))}
          {(r.cohorts ?? []).map((c) => (
            <Badge key={`c-${c}`} tone="neutral">
              {c}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{tc('table.actions', { defaultValue: 'Actions' })}</span>,
      align: 'end',
      cell: (r) => (
        <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
          <Pencil className="size-4" aria-hidden />
          <span className="sr-only md:not-sr-only md:text-xs">{t('editing')}</span>
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" aria-hidden />
          {t('new')}
        </Button>
      </header>
      <Card>
        <CardContent className="pt-6">
          <DataTable<FeatureFlag>
            data={visibleFlags}
            columns={columns}
            rowKey={(r) => r.key}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{ title: t('empty'), body: t('emptyBody') }}
            initialSort={{ id: 'key', direction: 'asc' }}
          />
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('new')}</DialogTitle>
            <DialogDescription>{t('subtitle')}</DialogDescription>
          </DialogHeader>
          <FeatureFlagForm
            onSaved={() => setCreating(false)}
            onCancel={() => setCreating(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('editing')}</DialogTitle>
            <DialogDescription>
              {t('editingBody', {
                defaultValue:
                  'Update rollout percent, scopes, and the enabled toggle. Key is immutable.',
              })}
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <FeatureFlagForm
              flag={editing}
              onSaved={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
